import { gql, useQuery } from '@apollo/client';
import { FormControl, FormLabel } from '@apollo/orbit';
import { Select } from '@apollo/space-kit/Select';
import React, { useCallback, useState } from 'react';

import { useIsAppInBackgroundContext } from 'src/app/appLayout/hooks/useIsAppInBackground';
import { insightsRouteConfig } from 'src/app/graph/routes';
import { DateRangeBadge } from 'src/components/dateRangeBadge/DateRangeBadge';
import {
  OverviewCardLoadingProps,
  OverviewLoading,
} from 'src/components/OverviewSection/OverviewLoading';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { useTrackCustomEvent } from 'src/hooks/useTrackCustomEvent';
import { assertUnreachable } from 'src/lib/assertUnreachable';
import { date } from 'src/lib/date/date';
import {
  SubgraphInsightsListFilterInput,
  SubgraphTopTrafficSourcesQuery,
  SubgraphTopTrafficSourcesQueryVariables,
} from 'src/lib/graphqlTypes/types';

import {
  ClientsTrafficSourceCard,
  OperationsTrafficSourceCard,
  cardSubtitles,
  cardTitles,
} from './TrafficSourceCard';
import {
  ClientTrafficSourceData,
  OperationTrafficSourceData,
  TRAFFIC_SOURCE_METRIC_KEYS,
  TRAFFIC_SOURCE_TYPES,
  TrafficSourceSortMetric,
  TrafficSourceType,
} from './types';
import {
  subgraphTrafficSparkLinesFragment,
  useSparklineMetrics,
} from './useSparklineMetrics';

// This could be moved to a dynamic number using react state but
// for now will keep as it is until we have product input on how
// we want users to select different ranges.
const SHOW_TOP_N = 5;

const topNSubgraphOperationDetailsFragment = gql`
  fragment TopNSubgraphOperationDetailsFragment on SubgraphInsightsListItem {
    operationId
    operationName
    operationType
  }
`;

const topNSubgraphClientDetailsFragment = gql`
  fragment TopNSubgraphClientDetailsFragment on SubgraphInsightsListItem {
    clientName
  }
`;

const subgraphTrafficTopOperationsFragment = gql`
  fragment SubgraphTrafficTopOperationsFragment on GraphVariant {
    id
    opsByFetchRate: subgraphInsightsList(
      first: $topN
      from: $timeFrom
      to: $timeTo
      filter: $insightsFilter
      # TODO: do we also need to group by operation type?  If so we'll need to add that to the API.
      groupBy: [OPERATION_ID, OPERATION_NAME]
      orderBy: { column: FETCH_COUNT_PER_MIN, direction: DESCENDING }
      requireLatencyMetrics: false
    ) {
      nodes {
        ...TopNSubgraphOperationDetailsFragment
        fetchCountPerMin
      }
    }

    opsByErrorPercentage: subgraphInsightsList(
      first: $topN
      from: $timeFrom
      to: $timeTo
      filter: $insightsFilter
      groupBy: [OPERATION_ID, OPERATION_NAME]
      orderBy: { column: FETCH_ERROR_PERCENTAGE, direction: DESCENDING }
      requireLatencyMetrics: true
    ) {
      nodes {
        ...TopNSubgraphOperationDetailsFragment
        fetchErrorPercentage
      }
    }

    opsByP95: subgraphInsightsList(
      first: $topN
      from: $timeFrom
      to: $timeTo
      filter: $insightsFilter
      groupBy: [OPERATION_ID, OPERATION_NAME]
      orderBy: { column: SERVICE_TIME_P95, direction: DESCENDING }
      requireLatencyMetrics: false
    ) {
      nodes {
        ...TopNSubgraphOperationDetailsFragment
        serviceTimeP95Ms
      }
    }
  }

  ${topNSubgraphOperationDetailsFragment}
`;

const subgraphTrafficTopClientsFragment = gql`
  fragment SubgraphTrafficTopClientsFragment on GraphVariant {
    id
    clientsByFetchRate: subgraphInsightsList(
      first: $topN
      from: $timeFrom
      to: $timeTo
      filter: $insightsFilter
      groupBy: [CLIENT_NAME]
      orderBy: { column: FETCH_COUNT_PER_MIN, direction: DESCENDING }
      requireLatencyMetrics: false
    ) {
      nodes {
        ...TopNSubgraphClientDetailsFragment
        fetchCountPerMin
      }
    }

    clientsByErrorPercentage: subgraphInsightsList(
      first: $topN
      from: $timeFrom
      to: $timeTo
      filter: $insightsFilter
      groupBy: [CLIENT_NAME]
      orderBy: { column: FETCH_ERROR_PERCENTAGE, direction: DESCENDING }
      requireLatencyMetrics: true
    ) {
      nodes {
        ...TopNSubgraphClientDetailsFragment
        fetchErrorPercentage
      }
    }

    clientsByP95: subgraphInsightsList(
      first: $topN
      from: $timeFrom
      to: $timeTo
      filter: $insightsFilter
      groupBy: [CLIENT_NAME]
      orderBy: { column: SERVICE_TIME_P95, direction: DESCENDING }
      requireLatencyMetrics: false
    ) {
      nodes {
        ...TopNSubgraphClientDetailsFragment
        serviceTimeP95Ms
      }
    }
  }

  ${topNSubgraphClientDetailsFragment}
`;

export const subgraphTopTrafficSourcesQuery = gql<
  SubgraphTopTrafficSourcesQuery,
  SubgraphTopTrafficSourcesQueryVariables
>`
  query SubgraphTopTrafficSourcesQuery(
    $graphId: ID!
    $graphVariant: String!
    $timeFrom: Timestamp!
    $timeTo: Timestamp!
    $topN: Int!
    $resolution: Resolution!
    $insightsFilter: SubgraphInsightsListFilterInput
    $forClients: Boolean!
  ) {
    service(id: $graphId) {
      id
      variant(name: $graphVariant) {
        id
        ...SubgraphTrafficSparkLinesFragment
        ...SubgraphTrafficTopOperationsFragment @skip(if: $forClients)
        ...SubgraphTrafficTopClientsFragment @include(if: $forClients)
      }
    }
  }

  ${subgraphTrafficSparkLinesFragment}
  ${subgraphTrafficTopOperationsFragment}
  ${subgraphTrafficTopClientsFragment}
`;

function getTopNOperationStats<T extends TrafficSourceSortMetric>(
  data: SubgraphTopTrafficSourcesQuery,
  metric: T,
): OperationTrafficSourceData<T>[] {
  switch (metric) {
    case 'fetchCountPerMin': {
      return (
        data.service?.variant?.opsByFetchRate?.nodes?.filter(
          (node) => node.fetchCountPerMin > 0,
        ) || []
      );
    }
    case 'fetchErrorPercentage': {
      return (
        data.service?.variant?.opsByErrorPercentage?.nodes?.filter(
          (node) => node.fetchErrorPercentage > 0,
        ) || []
      );
    }
    case 'serviceTimeP95Ms': {
      return (
        data.service?.variant?.opsByP95?.nodes?.filter(
          (node) => node.serviceTimeP95Ms && node.serviceTimeP95Ms > 0,
        ) || []
      );
    }
    default:
      assertUnreachable(metric);
  }
}

function getTopNClientStats<T extends TrafficSourceSortMetric>(
  data: SubgraphTopTrafficSourcesQuery,
  metric: T,
): ClientTrafficSourceData<T>[] {
  switch (metric) {
    case 'fetchCountPerMin': {
      return (
        data.service?.variant?.clientsByFetchRate?.nodes?.filter(
          (node) => node.fetchCountPerMin > 0,
        ) || []
      );
    }
    case 'fetchErrorPercentage': {
      return (
        data.service?.variant?.clientsByErrorPercentage?.nodes?.filter(
          (node) => node.fetchErrorPercentage > 0,
        ) || []
      );
    }
    case 'serviceTimeP95Ms': {
      return (
        data.service?.variant?.clientsByP95?.nodes?.filter(
          (node) => node.serviceTimeP95Ms && node.serviceTimeP95Ms > 0,
        ) || []
      );
    }
    default:
      assertUnreachable(metric);
  }
}

const topTrafficSourcesLoadingCards: {
  operations: OverviewCardLoadingProps[];
  clients: OverviewCardLoadingProps[];
} = {
  operations: [
    {
      title: cardTitles.fetchCountPerMin,
      subtitle: cardSubtitles.operations.fetchCountPerMin,
    },
    {
      title: cardTitles.fetchErrorPercentage,
      subtitle: cardSubtitles.operations.fetchErrorPercentage,
    },
    {
      title: cardTitles.serviceTimeP95Ms,
      subtitle: cardSubtitles.operations.serviceTimeP95Ms,
    },
  ],
  clients: [
    {
      title: cardTitles.fetchCountPerMin,
      subtitle: cardSubtitles.clients.fetchCountPerMin,
    },
    {
      title: cardTitles.fetchErrorPercentage,
      subtitle: cardSubtitles.clients.fetchErrorPercentage,
    },
    {
      title: cardTitles.serviceTimeP95Ms,
      subtitle: cardSubtitles.clients.serviceTimeP95Ms,
    },
  ],
};

export function TopTrafficSourcesSection({
  graphId,
  filter,
}: {
  graphId: string;
  filter: SubgraphInsightsListFilterInput;
}) {
  const {
    to,
    from,
    range: timeRange,
    graphVariant,
    fastModeRange,
  } = useRouteParams(insightsRouteConfig);
  const [trafficSourceType, setTrafficSourceType] = useState<TrafficSourceType>(
    TRAFFIC_SOURCE_TYPES.operations,
  );
  const trackCustomEvent = useTrackCustomEvent();
  const onTrafficSourceTypeChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const newValue = event.target.value as TrafficSourceType;
      trackCustomEvent({
        action: 'user_action',
        category: 'Subgraph Insights',
        label: `Change top traffic view: ${newValue}`,
      });
      setTrafficSourceType(newValue);
    },
    [trackCustomEvent],
  );

  const range = timeRange ?? 'lastDay';
  const isInBackground = useIsAppInBackgroundContext();
  const { timeFrom, timeTo, resolution, pollInterval } = date.getDateBounds(
    range,
    { from, to },
    undefined,
    fastModeRange,
  );

  const { data, loading } = useQuery(subgraphTopTrafficSourcesQuery, {
    variables: {
      graphId,
      timeFrom,
      timeTo,
      graphVariant,
      topN: SHOW_TOP_N,
      resolution,
      insightsFilter: filter,
      forClients: trafficSourceType === TRAFFIC_SOURCE_TYPES.clients,
    },
    skip: isInBackground,
    pollInterval,
    skipPollAttempt: useCallback(() => isInBackground, [isInBackground]),
  });

  const sparklineData = data?.service?.variant?.sparklines;
  const { sparklineMetrics, min, max, average } =
    useSparklineMetrics(sparklineData);
  const loadingCards =
    trafficSourceType === TRAFFIC_SOURCE_TYPES.operations
      ? topTrafficSourcesLoadingCards.operations
      : topTrafficSourcesLoadingCards.clients;

  if (loading) {
    // TBD: consider pushing the loading state into the return body
    return (
      <OverviewLoading
        cards={loadingCards}
        heading={
          <Select
            value={trafficSourceType}
            feel="flat"
            className="self-center font-heading text-xl font-medium text-heading"
            placement="bottom-start"
          >
            <option className="font-normal" value="operations">
              Top operations
            </option>
            <option className="font-normal" value="clients">
              Top clients
            </option>
          </Select>
        }
      />
    );
  }

  if (!data?.service) {
    return <OverviewLoading loadingError cards={loadingCards} />;
  }

  const topNOperationStats = getTopNOperationStats(data, 'fetchCountPerMin');
  const topNClientStats = getTopNClientStats(data, 'fetchCountPerMin');

  // Determine if this is a "no data" state using fetch rate data. We can return
  // null since SubgraphInsightsPage displays the NoData component
  const hasData = topNOperationStats.length > 0 || topNClientStats.length > 0;
  if (!hasData) {
    return null;
  }

  return (
    <div>
      <div>
        <div className="mb-4 inline-flex items-center gap-2">
          <FormControl>
            <FormLabel className="sr-only">
              {trafficSourceType === TRAFFIC_SOURCE_TYPES.operations
                ? 'Top operations'
                : 'Top clients'}
            </FormLabel>
            <Select
              value={trafficSourceType}
              onChange={onTrafficSourceTypeChange}
              feel="flat"
              className="self-center font-heading text-xl font-medium text-heading"
              placement="bottom-start"
            >
              <option className="font-normal" value="operations">
                Top operations
              </option>
              <option className="font-normal" value="clients">
                Top clients
              </option>
            </Select>
          </FormControl>
          <DateRangeBadge />
        </div>
      </div>
      <div className="grid w-full grid-cols-1 gap-6 @5xl:grid-cols-3">
        {TRAFFIC_SOURCE_METRIC_KEYS.map((metric: TrafficSourceSortMetric) =>
          trafficSourceType === TRAFFIC_SOURCE_TYPES.operations ? (
            <OperationsTrafficSourceCard
              key={metric}
              currentValue={average[metric] || 0}
              pastValue={null}
              sortMetric={metric}
              min={min[metric] || 0}
              max={max[metric] || 0}
              sparklineData={sparklineMetrics[metric]}
              topSources={getTopNOperationStats(data, metric)}
              timeRange={range}
            />
          ) : (
            <ClientsTrafficSourceCard
              key={metric}
              currentValue={average[metric] || 0}
              pastValue={null}
              sortMetric={metric}
              min={min[metric] || 0}
              max={max[metric] || 0}
              sparklineData={sparklineMetrics[metric]}
              topSources={getTopNClientStats(data, metric)}
              timeRange={range}
            />
          ),
        )}
      </div>
    </div>
  );
}
